import React, {useEffect, useRef, useState} from 'react'
import ProductImageSmall from "../../../pod/components/product-upload/product-image-small";
import {DocumentTextIcon, EyeIcon, EyeSlashIcon, PlusIcon} from "@heroicons/react/24/solid";
import {PhotoIcon, SwatchIcon} from "@heroicons/react/20/solid";
import SelectDesignProductImages from "../../../shopify/components/design-info/select-design-product-images";
import DeleteItem from "../../../common/ui/delete-item";
import SelectColorTypeDetails from "../../../design/components/design-editor/settings/select-color-type-details";
import EditFontDetails from "../../../design/components/design-editor/settings/edit-font-details";
import EditStrokeDetails from "../../../design/components/design-editor/settings/edit-stroke-details";
import EditShadowDetails from "../../../design/components/design-editor/settings/edit-shadow-details";
import EditRotationDetails from "../../../design/components/design-editor/settings/edit-rotation-details";
import EditTextPathDetails from "../../../design/components/design-editor/settings/edit-text-path-details";
import EditFlipDetails from "../../../design/components/design-editor/settings/edit-flip-details";
import RenderFields from "../../../common/ui/render-fields";
import _ from "lodash";
import {DEFAULT_COLOR_SETUP, DEFAULT_TEXT_SETTINGS, IMAGE_FIELD_LIST, KEY_PREFIX} from "../../constants/media.constant";
import {getNextItemKey} from "../../../common/utils/item-key-util";
import {handleFieldValueChanges} from "../../../common/utils/field-change-util";
import {processColorDetails} from "../../../common/utils/color-util";
import {processTextPathData} from "../../../design/utils/svg-data.util";
import {FLIP_DIRECTION_KEY, TEXT_POSITION_FIELD_LIST} from "../../../design/constants/design.constant";
import {generateLinearGradientDetail} from "../../../common/utils/tailwind-color-util";
import {processBackgroundDetails} from "../../../design/utils/background-util";
import {processLogoDetails} from "../../../shopify/utils/logo-util";
import {processImagePositionDetails} from "../../../shopify/utils/position-util";

const ShowSingleCardLayers = ({
                                  card,
                                  settings,
                                  allDesigns,
                                  allDesignInfo,
                                  afterCardChanges
                              }) => {
    const [layers, setLayers] = useState(card?.layers);
    const [selectedLayer = {}, setSelectedLayer] = useState(null);
    const [showBackgroundColorSettings, setShowBackgroundColorSettings] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showTextSettings, setShowTextSettings] = useState(false);

    useEffect(() => {
        handleLayerChanges();
    }, [layers]);

    const handleLayerChanges = () => {
        const existingLayers = [...layers];
        const existingCard = {...card};
        existingCard.layers = existingLayers;
        afterCardChanges(existingCard);
    }

    const addNewLayer = () => {
        const existingLayers = [...layers];

        existingLayers.push({
            name: 'Layer ' + (existingLayers?.length + 1),
            key: getNextItemKey(existingLayers, KEY_PREFIX.LAYER),
            displayOrder: existingLayers?.length + 1,
        });

        setLayers(existingLayers);
    }

    const handleTextDetailChanges = (event, layerIndex, childIndex) => {
        const {name, value, type} = event.target;
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        let existingChild = existingLayer?.children[childIndex];

        existingChild = handleFieldValueChanges(event, existingChild);

        if (name === 'colorKey' || name === 'colorCodeKey' ||
            name === 'strokeColorKey' || name === 'strokeColorCodeKey' ||
            name === 'shadowColorKey' || name === 'shadowColorCodeKey') {
            processColorDetails(existingChild);
        } else if (name === 'radius' || name === 'rotation' || name === 'letterSpacing' ||
            name === 'startAngle' || name === 'sweepAngle') {
            processTextPathData(existingChild);
        } else if (name === 'offsetXPercentage') {
            let fillPatternOffsetXPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetXPercentage)) {
                fillPatternOffsetXPercentage = 0;
            }

            existingChild.fillPatternOffsetX = -existingChild.totalWidth * fillPatternOffsetXPercentage / 100;
            existingChild.offsetX = existingChild.fillPatternOffsetX;
        } else if (name === 'offsetYPercentage') {
            let fillPatternOffsetYPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetYPercentage)) {
                fillPatternOffsetYPercentage = 0;
            }

            existingChild.fillPatternOffsetY = existingChild.totalWidth * fillPatternOffsetYPercentage / 100;
            existingChild.offsetY = existingChild.fillPatternOffsetY;
        }

        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const handleImageDetailChanges = (event, layerIndex, childIndex) => {
        const {name, value, type} = event.target;
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        let existingChild = existingLayer?.children[childIndex];
        existingChild = handleFieldValueChanges(event, existingChild);
        const existingChildProps = existingChild?.props || {};

        if (name === 'flip') {
            if (value === FLIP_DIRECTION_KEY.HORIZONTAL) {
                existingChildProps.scaleX = -existingChildProps.scaleX;
            } else if (value === FLIP_DIRECTION_KEY.VERTICAL) {
                existingChildProps.scaleY = -existingChildProps.scaleY;
            }
        } else if (name === 'size') {
            let size = _.parseInt(value);
            if (_.isNaN(size)) {
                size = 100;
            }

            existingChildProps.width = existingChildProps.totalWidth * size / 100;
            existingChildProps.height = existingChildProps.totalHeight * size / 100;
        } else if (name === 'offsetXPercentage') {
            let fillPatternOffsetXPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetXPercentage)) {
                fillPatternOffsetXPercentage = 0;
            }

            existingChildProps.fillPatternOffsetX = -existingChildProps.width * fillPatternOffsetXPercentage / 100;
            existingChildProps.offsetX = existingChildProps.fillPatternOffsetX || 0;
        } else if (name === 'offsetYPercentage') {
            let fillPatternOffsetYPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetYPercentage)) {
                fillPatternOffsetYPercentage = 0;
            }

            existingChildProps.fillPatternOffsetY = existingChildProps.width * fillPatternOffsetYPercentage / 100;
            existingChildProps.offsetY = existingChildProps.fillPatternOffsetY || 0;
        } else if (name === 'rotation') {
            let rotationPercentage = _.parseInt(value);
            if (_.isNaN(rotationPercentage)) {
                rotationPercentage = 0;
            }

            existingChildProps.fillPatternRotation = rotationPercentage;
            existingChildProps.rotation = rotationPercentage;
        } else if (name === 'fit') {
            let fillPatternScale = settings?.height / existingChild?.height;

            if (value === 'height') {
                fillPatternScale = settings?.height / existingChild?.height;
            } else if (value === 'width') {
                fillPatternScale = settings?.width / existingChild?.width;
            }

            existingChildProps.fillPatternScaleX = fillPatternScale;
            existingChildProps.fillPatternScaleY = fillPatternScale;
            existingChildProps.width = existingChild?.width * fillPatternScale;
            existingChildProps.height = existingChild?.height * fillPatternScale;

            if (value === 'width') {
                existingChildProps.y = (settings?.height - existingChildProps?.height) / 2;
            } else if (value === 'height') {
                existingChildProps.x = (settings?.width - existingChildProps?.width) / 2;
            }

            existingChildProps.offsetX = existingChildProps.fillPatternOffsetX || 0;
            existingChildProps.offsetY = existingChildProps.fillPatternOffsetY || 0;

            processImagePositionDetails('center-center', existingChildProps, settings);

        } else if (name === 'positionType') {
            processImagePositionDetails(value, existingChildProps, settings);
        }
        
        existingChild.props = existingChildProps;
        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    /*const fitImage = (productImage, imageProps, productConfig, settings) => {
        let fillPatternScale = settings?.height / productImage?.height;

        if (productConfig.fit === 'height') {
            fillPatternScale = settings?.height / productImage?.height;
        } else if (productConfig.fit === 'width') {
            fillPatternScale = settings?.width / productImage?.width;
        }

        imageProps.fillPatternScaleX = fillPatternScale;
        imageProps.fillPatternScaleY = fillPatternScale;
        imageProps.width = productImage?.width * fillPatternScale;
        imageProps.height = productImage?.height * fillPatternScale;

        if (productConfig.fit === 'width') {
            imageProps.y = (settings?.height - imageProps?.height) / 2;
        } else if (productConfig.fit === 'height') {
            imageProps.x = (settings?.width - imageProps?.width) / 2;
        }

        imageProps.fillPatternOffsetX = -(imageProps.width * productConfig.fillPatternOffsetXPercentage / 100);
        imageProps.fillPatternOffsetY = (imageProps.height * productConfig.fillPatternOffsetYPercentage / 100);
    }*/

    const removeLayer = (layer) => {
        const existingLayers = [...layers];
        const filteredLayers = existingLayers?.filter((existingLayer) => existingLayer?.key !== layer?.key);
        setLayers(filteredLayers);
    }

    const toggleLayerVisibility = (layerIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        existingLayer.isHidden = !existingLayer?.isHidden;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const addNewText = (layer) => {
        const currentLayer = {...selectedLayer};

        if (currentLayer?.key !== layer?.key) {
            setShowTextSettings(true);
        } else {
            setShowTextSettings(!showTextSettings);
        }

        const newText = {
            isText: true,
            isEditable: true,
            layerKey: layer?.key,
            key: getNextItemKey(currentLayer?.children, KEY_PREFIX.TEXT),
            displayOrder: 0,
            text: 'New Text',
            ...DEFAULT_TEXT_SETTINGS
        }

        if (!layer?.children) {
            layer.children = [];
        }

        layer.children.push(newText);
        layer.children = _.sortBy(layer?.children, ['displayOrder']);

        const existingLayers = [...layers];
        const currentLayerIndex = _.findIndex(existingLayers, (layer) => layer?.key === currentLayer?.key);
        existingLayers[currentLayerIndex] = currentLayer;
        setLayers(existingLayers);

        setSelectedLayer(layer);
    }

    const showOrHideBackgroundColorSettings = (layer) => {
        const currentLayer = {...selectedLayer};

        if (currentLayer?.key !== layer?.key) {
            setShowBackgroundColorSettings(true);
        } else {
            setShowBackgroundColorSettings(!showBackgroundColorSettings);
        }

        const newBackground = {
            isBackground: true,
            layerKey: layer?.key,
            key: getNextItemKey(currentLayer?.children, KEY_PREFIX.BACKGROUND),
            displayOrder: 0,
            settings: {
                height: settings?.height,
                width: settings?.width,
                background: DEFAULT_COLOR_SETUP,
            }
        }

        if (!layer?.children) {
            layer.children = [];

            const linearGradientDetail = generateLinearGradientDetail();
            newBackground.settings.background = {
                ...newBackground.settings.background,
                ...linearGradientDetail
            };

            processBackgroundDetails(newBackground?.settings?.background, newBackground.settings);
        }

        layer.children.push(newBackground);
        layer.children = _.sortBy(layer?.children, ['displayOrder']);

        const existingLayers = [...layers];
        const currentLayerIndex = _.findIndex(existingLayers, (layer) => layer?.key === currentLayer?.key);
        existingLayers[currentLayerIndex] = currentLayer;
        setLayers(existingLayers);

        setSelectedLayer(layer);
    }

    const toggleChildEditable = (layerIndex, childIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        const existingChildItem = existingLayer?.children[childIndex];
        existingChildItem.isEditable = !existingChildItem?.isEditable;
        existingLayer.children[childIndex] = existingChildItem;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const generateRandomBackgroundColor = (layerIndex, childIndex) => {
        const linearGradientDetail = generateLinearGradientDetail();

        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        const existingChild = existingLayer?.children[childIndex];
        existingChild.settings.background = {
            ...existingChild.settings.background,
            ...linearGradientDetail
        };

        processBackgroundDetails(existingChild?.settings?.background, existingChild.settings);

        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const removeChild = (layerIndex, childItemIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        existingLayer.children = existingLayer?.children?.filter((childItem, index) => index !== childItemIndex);
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const handleSettingsChanges = (event, layerIndex, childIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        const existingChild = existingLayer?.children[childIndex];

        let existingSettings = existingChild?.settings || {};

        existingSettings = handleFieldValueChanges(event, existingSettings);
        existingChild.settings = existingSettings;

        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    return (
        <>
            <div
                className="h-full content-start justify-start overflow-y-scroll mx-2 p-4 border border-gray-400 bg-gray-200 rounded-lg">

                <div className={'flex justify-between items-center'}>
                    <div className={'text-lg font-semibold'}>Layers</div>
                    <div>
                        <button
                            className="button-gray-outline pl-2 pr-3 py-1 flex justify-center items-center font-semibold"
                            onClick={addNewLayer}>
                            <PlusIcon className="h-6 w-6 mr-1"/>
                            New Layer
                        </button>
                    </div>
                </div>

                <div className={'mt-4'}>
                    {layers?.map((layer, layerIndex) => (
                        <div key={layerIndex}
                             className={`border bg-white px-3 py-2 rounded my-2 ${selectedLayer?.key === layer?.key ? 'bg-purple-100 border-purple-400' : 'border-gray-200'}`}>

                            <div className={'flex justify-between items-center my-2 mx-2'}>
                                <div
                                    onClick={() => {
                                        setSelectedLayer(layer);
                                    }}
                                    className={'text-purple-500 cursor-pointer text-lg font-semibold'}>Layer {layerIndex + 1}
                                    {layer?.isCommonTextLayer && ` (Common Texts)`}
                                </div>

                                <div className={'flex justify-center items-center'}>
                                    <button
                                        className="button-outline-green px-2 py-1 flex justify-center items-center mr-2"
                                        onClick={() => {
                                            showOrHideBackgroundColorSettings(layer);
                                        }}>
                                        <SwatchIcon className="h-5 w-5"/>
                                    </button>

                                    <button
                                        className="button-sky-outline px-2 py-1 flex justify-center items-center mr-2"
                                        onClick={() => {
                                            addNewText(layer);
                                        }}>
                                        <DocumentTextIcon className="h-5 w-5"/>
                                    </button>

                                    {/*<SelectDesignProductImages
                                        allDesignInfo={allDesignInfo}
                                        allDesigns={allDesigns}
                                        layer={layer}
                                        mediaFor={{}}
                                        handleSelectedProductImageInLayer={(event) => {
                                            console.log('handleSelectedProductImageInLayer', event);
                                        }}/>*/}
                                </div>

                                <button
                                    onClick={() => {
                                        toggleLayerVisibility(layerIndex);
                                    }}
                                    className="button-outline-sky p-2">
                                    {!layer?.isHidden &&
                                        <EyeIcon className="h-6 w-6 text-green-500"/>
                                    }

                                    {layer?.isHidden &&
                                        <EyeSlashIcon className="h-6 w-6 text-red-500"/>
                                    }
                                </button>

                                <DeleteItem
                                    handleDeleteItem={() => {
                                        removeLayer(layer);
                                    }}
                                    iconSize={'h-5 w-5'}
                                    itemIndex={layerIndex}
                                    showIcon={true}/>
                            </div>

                            {layer?.children?.length > 0 &&
                                <div className={'mt-4'}>
                                    {layer?.children?.map((child, childIndex) => (

                                        <div key={childIndex}
                                             className={'border border-gray-200 p-2 m-2 rounded bg-gray-100'}>

                                            <div
                                                className={'flex justify-between items-center'}>

                                                {child?.isImage &&
                                                    <div onClick={() => {
                                                        setSelectedImage(child);
                                                        toggleChildEditable(layerIndex, childIndex);
                                                    }}>
                                                        <ProductImageSmall
                                                            afterImageSelected={(event) => {
                                                            }}
                                                            dimension={40}
                                                            isSelected={selectedImage?.id === child?.id}
                                                            key={`selected-image-${childIndex}`}
                                                            imageUrl={child?.url}/>
                                                    </div>
                                                }

                                                {child?.isText &&
                                                    <>
                                                        <div
                                                            className="text-purple-500 cursor-pointer ml-2 py-2 font-semibold"
                                                            onClick={() => {
                                                                setSelectedImage(null);
                                                                toggleChildEditable(layerIndex, childIndex);
                                                            }}>
                                                            {child?.text}
                                                        </div>
                                                    </>
                                                }

                                                {child?.isBackground &&
                                                    <>
                                                        <div
                                                            className="text-purple-500 cursor-pointer ml-2 py-2 font-semibold"
                                                            onClick={() => {
                                                                setSelectedImage(null);
                                                                setShowBackgroundColorSettings(!showBackgroundColorSettings);
                                                            }}>
                                                            Background Color & Shade
                                                        </div>

                                                        <button
                                                            className="button-sky-outline px-2 py-1 flex justify-center items-center"
                                                            onClick={() => {
                                                                generateRandomBackgroundColor(layerIndex, childIndex);
                                                            }}>
                                                            Random Color
                                                        </button>
                                                    </>
                                                }

                                                {selectedLayer?.key === layer?.key &&
                                                    <div className={'mt-1 mr-1'}>
                                                        <DeleteItem
                                                            handleDeleteItem={() => {
                                                                removeChild(layerIndex, childIndex);
                                                            }}
                                                            iconSize={'h-4 w-4'}
                                                            itemIndex={childIndex}
                                                            showIcon={true}/>
                                                    </div>
                                                }
                                            </div>

                                            <div>
                                                {child?.isBackground && showBackgroundColorSettings &&
                                                    <>
                                                        <div className={''}>
                                                            <SelectColorTypeDetails
                                                                hideLabel={true}
                                                                label={'Background Color & Shade'}
                                                                parentForm={child?.settings?.background}
                                                                colorAttribute={'background'}
                                                                colorKeyAttribute={'backgroundColorKey'}
                                                                colorCodeKeyAttribute={'backgroundColorCodeKey'}
                                                                settings={child?.settings}
                                                                handleColorDetailsChanges={(event) => {
                                                                    handleSettingsChanges(event, layerIndex, childIndex);
                                                                }}
                                                                handleClearColorDetailsChanges={(event) => {
                                                                }}/>
                                                        </div>
                                                    </>
                                                }
                                            </div>

                                            <div>
                                                {child?.isText && child?.isEditable && !child?.isCommonText &&
                                                    <>
                                                        <div className="mt-2">

                                                            <div
                                                                className="field-container w-full">
                                                                <div
                                                                    className="field-label">Text
                                                                </div>
                                                                <textarea
                                                                    id="text"
                                                                    name="text"
                                                                    value={child?.text}
                                                                    onChange={(event) => {
                                                                        handleTextDetailChanges(event, layerIndex, childIndex);
                                                                    }}
                                                                    className="text-area-field h-28"/>
                                                            </div>

                                                            <EditFontDetails
                                                                displayTextItem={child}
                                                                handleFontDetailsChanges={(event) => {
                                                                    handleTextDetailChanges(event, layerIndex, childIndex);
                                                                }}/>

                                                            <SelectColorTypeDetails
                                                                label={'Text Color & Shade'}
                                                                parentForm={child}
                                                                colorAttribute={'color'}
                                                                colorKeyAttribute={'colorKey'}
                                                                colorCodeKeyAttribute={'colorCodeKey'}
                                                                settings={child?.settings}
                                                                handleColorDetailsChanges={(event) => {
                                                                    handleTextDetailChanges(event, layerIndex, childIndex);
                                                                }}
                                                                handleClearColorDetailsChanges={() => {
                                                                }}/>

                                                            <EditStrokeDetails
                                                                parentForm={child}
                                                                handleStrokeDetailsChanges={(event) => {
                                                                    handleTextDetailChanges(event, layerIndex, childIndex);
                                                                }}/>

                                                            <EditShadowDetails
                                                                parentForm={child}
                                                                handleShadowDetailsChanges={(event) => {
                                                                    handleTextDetailChanges(event, layerIndex, childIndex);
                                                                }}/>

                                                            <EditRotationDetails
                                                                parentItem={child}
                                                                handleRotationDetailsChanges={(event) => {
                                                                    handleTextDetailChanges(event, layerIndex, childIndex);
                                                                }}/>

                                                            <EditTextPathDetails
                                                                parentItem={child}
                                                                handleTextPathDetailsChanges={(event) => {
                                                                    handleTextDetailChanges(event, layerIndex, childIndex);
                                                                }}/>

                                                            <div className="flex flex-wrap justify-start">
                                                                <RenderFields
                                                                    parentForm={child}
                                                                    handleDetailChanges={(event) => {
                                                                        handleTextDetailChanges(event, layerIndex, childIndex);
                                                                    }}
                                                                    fieldList={TEXT_POSITION_FIELD_LIST}
                                                                    enableEdit={true}
                                                                    fieldClassName={'w-1/2'}/>
                                                            </div>
                                                        </div>
                                                    </>
                                                }
                                            </div>

                                            <div>
                                                {child?.isImage && child?.isEditable &&
                                                    <>
                                                        <EditFlipDetails
                                                            item={child}
                                                            handleFlipDetailsChanges={(event) => {
                                                                handleImageDetailChanges(event, layerIndex, childIndex);
                                                            }}/>

                                                        <div
                                                            className="flex flex-wrap justify-start">
                                                            <RenderFields
                                                                parentForm={child}
                                                                handleDetailChanges={(event) => {
                                                                    handleImageDetailChanges(event, layerIndex, childIndex);
                                                                }}
                                                                fieldList={_.groupBy(IMAGE_FIELD_LIST, 'group')['dimension']}
                                                                enableEdit={true}
                                                                fieldClassName={'w-1/2'}/>
                                                        </div>
                                                    </>
                                                }
                                            </div>

                                        </div>

                                    ))}
                                </div>
                            }

                        </div>
                    ))}
                </div>

            </div>
        </>
    )
}

export default ShowSingleCardLayers
